import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { BookOpen, ArrowLeft, Clock, Calendar } from "lucide-react";

interface BlogMetadata {
  id: string;
  title: string;
  date: string;
  readTime: string;
  category: string;
  htmlFile: string;
}

const blogMetadata: Record<string, BlogMetadata> = {
  "3-mental-math-tricks": {
    id: "3-mental-math-tricks",
    title: "3 Mental Math Tricks to Unlock Your Inner Superpower",
    date: "November 3, 2025",
    readTime: "5 min read",
    category: "Mental Math",
    htmlFile: "3-Mental_math_Tricks.html",
  },
  "ithopu-how-it-helps-mental-math": {
    id: "ithopu-how-it-helps-mental-math",
    title: "How Ithopu Helps Master Mental Math",
    date: "November 3, 2025",
    readTime: "6 min read",
    category: "Learning Platform",
    htmlFile: "ithopu-how-it-helps-mental-math.html",
  },
  "ithopu-vedic-maths-tricks": {
    id: "ithopu-vedic-maths-tricks",
    title: "Vedic Maths Tricks: Ancient Wisdom for Modern Learning",
    date: "November 3, 2025",
    readTime: "8 min read",
    category: "Vedic Math",
    htmlFile: "ithopu-vedic-maths-tricks.html",
  },
};

export default function BlogPost() {
  const { id } = useParams<{ id: string }>();
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const metadata = id ? blogMetadata[id] : null;

  useEffect(() => {
    if (!metadata) {
      setLoading(false);
      return;
    }

    // Load the HTML file
    fetch(`/blogs/${metadata.htmlFile}`)
      .then((response) => response.text())
      .then((html) => {
        // Extract just the body content (remove html, head, body tags)
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const bodyContent = doc.body.innerHTML;
        setHtmlContent(bodyContent);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading blog post:", error);
        setLoading(false);
      });
  }, [metadata]);

  if (!metadata) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <BookOpen className="h-6 w-6 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">Ithopu</span>
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </div>
        </div>
      </header>

      {/* Blog Content */}
      <article className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Metadata */}
        <div className="mb-8">
          <span className="inline-block text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full mb-4">
            {metadata.category}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {metadata.title}
          </h1>
          <div className="flex items-center space-x-6 text-gray-600">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              <span className="text-sm">{metadata.date}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              <span className="text-sm">{metadata.readTime}</span>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-xl text-gray-600">Loading...</div>
          </div>
        )}

        {/* Blog Content */}
        {!loading && (
          <div
            className="prose prose-lg max-w-none bg-white rounded-lg shadow-sm p-8 md:p-12"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        )}

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Learning?</h2>
          <p className="text-xl text-blue-100 mb-6">
            Join Ithopu and master mental math with interactive tutorials
          </p>
          <Link
            to="/register"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            Get Started Free
          </Link>
        </div>
      </article>
    </div>
  );
}
